import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AddNoteRightAction } from '@/components/order-guide/add-note-right-action';
import {
  APP_CONTAINER_ROUTES,
  CATALOG_ROUTES,
  ORDER_GUIDE_ROUTES,
} from '@/navigators/routes';
import { CatalogCategoryList } from '@/components/catalog-search/catalog-category-list';
import {
  CatalogCategoryType,
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { CatalogStackParams } from '@/navigators/types';
import {
  CategoryTabs,
  LIST_HEIGHT,
} from '@/components/catalog-search/category-tabs';
import { createStyles } from '@/contexts/theme-context';
import { EssentialItemRightAction } from '@/components/order-guide/essential-item-right-action';
import { getActiveRoute } from '@/utils/navigation';
import { ItemSearchInput } from '@/components/item-search-input';
import { KeyboardAvoidingView, Platform, TextInput, View } from 'react-native';

import { CategoryTabsSkeleton } from '@/components/catalog-search/category-tabs-skeleton';
import {
  Props as ListItemProps,
  SwipeableWithInstantClose,
} from '@/components/list-item';
import { PageContainer } from '@/components/page-container';
import { SearchAddNoteActionSheet } from '@/components/order-guide/search-add-note-action-sheet';
import { SearchCatalogContent } from '@/components/search-catalog-content';
import { SearchEntryPoint, UnifiedSearchScreenProps } from './types';
import { SearchFilterPills } from '@/components/search-filters/search-filter-pills';
import { SearchNote } from '@/components/order-guide/search-note';
import { StackScreenProps } from '@react-navigation/stack';
import { UnifiedSearchEmptyState } from '@/components/order-guide/unified-search-empty-state';
import { useCanWriteLineItemRequest } from '@/hooks/dynamic-config/use-can-write-line-item-request';
import { useCatalogCategoryListsEnabled } from '@/hooks/dynamic-config/use-catalog-category-lists-enabled';
import { useCategoryCountsMap } from '@/hooks/use-category-counts-map';
import { useConsolidatedCatalogFilters } from '@/hooks/catalog-search/use-consolidated-catalog-filters';
import { useFilteredCategoryCatalog } from '@/hooks/catalog-search/use-filtered-category-catalog';
import { useFilteredSearchCatalog } from '@/hooks/catalog-search/use-filtered-search-catalog';
import { useFocusEffect } from '@react-navigation/native';
import { useGeneratedCategories } from '@/hooks/use-generated-categories';
import { useItemGroupBadgeFilters } from '@/hooks/use-item-group-badge-filters';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSearchOrderGuideActions } from '@/hooks/use-search-order-guide-actions';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';
import { ViewOrderButton } from '@/components/partners/view-order-button';

interface SearchScreenComponentProps {
  category?: CatalogCategoryType;
  navigation:
    | UnifiedSearchScreenProps['navigation']
    | SearchScreenProps['navigation'];
  searchEntryPoint?: SearchEntryPoint;
  showCategories?: boolean;
}

const ACTION_BUTTON_WIDTH = 72;

export const SearchScreenComponent: React.FC<SearchScreenComponentProps> = ({
  category,
  navigation,
  searchEntryPoint,
  showCategories,
}) => {
  const { isSmallTabletLayout } = useLayoutContext();
  const [styles] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const searchInputRef = useRef<TextInput>(null);
  const { variantPackPartitionMap, partitionToCollectionMap } =
    useOrderGuideItemsContext();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { editEssentialItem } = useUpdateOrderGuideItemContext();

  const { categories: generatedCategories, loading: categoriesLoading } =
    useGeneratedCategories({ skip: !showCategories });
  const categories = showCategories ? generatedCategories : undefined;

  const catalogCategoryListsEnabled = useCatalogCategoryListsEnabled();
  const canWriteRequest = useCanWriteLineItemRequest();

  const swipeableItemRef = useRef<SwipeableWithInstantClose | null>(null);

  const closeSwipeableRef = useCallback(() => {
    swipeableItemRef.current?.close();
    swipeableItemRef.current = null;
  }, []);

  const [selectedCategory, setSelectedCategory] = useState<
    CatalogCategoryType | undefined
  >(category);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const searchCatalog = useFilteredSearchCatalog({
    category: selectedCategory,
    searchEntryPoint,
  });
  const categoryCatalog = useFilteredCategoryCatalog({
    category: catalogCategoryListsEnabled ? undefined : selectedCategory,
  });
  const { badges } = useItemGroupBadgeFilters({
    category: selectedCategory,
    items: catalogCategoryListsEnabled
      ? (searchCatalog.items ?? undefined)
      : categoryCatalog.listData,
  });

  const orderGuideUnifiedSearchEnabled = useSupplierDynamicConfigValue(
    'order_guide_unified_search_enabled',
  );

  const { addNoteState, handleAddNotePress, setAddNoteState } =
    useSearchOrderGuideActions({ closeSwipeableRef });

  const searchOrderGuideActionsEnabled =
    orderGuideUnifiedSearchEnabled && customOrderGuideEditingEnabled;

  const swipeableActionsEnabled =
    searchOrderGuideActionsEnabled && Platform.OS !== 'web';

  const {
    changeSortOrder,
    clearSearch,
    items,
    queryLoading,
    queryRef,
    searchFilters,
    searchItems,
    searchResults,
    searchSortOrder,
    userHasSearched,
  } = searchCatalog;

  const searchText = queryRef.current;
  const showSearchView =
    Boolean(searchText) ||
    Boolean(searchFilters.selected.length) ||
    (isSearchFocused && !selectedCategory);
  const isEmpty = items?.length === 0 || categoryCatalog.listData.length === 0;

  const categoryCountsMap = useCategoryCountsMap({
    categories: categories ?? [],
    searchResults: searchResults ?? [],
  });

  const consolidatedFilters = useConsolidatedCatalogFilters({
    badges,
    catalogCategoryListsEnabled,
    categoryCatalog,
    searchCatalog,
    showSearchView,
  });

  // Hide filter UI when:
  // - No search text AND
  // - No user-selected filters (default filters don't count until user has searched) AND
  // - Either focused on search or no category selected
  const hasUserSelectedFilters =
    userHasSearched && Boolean(searchFilters.selected.length);
  const catalogFilters =
    !searchText &&
    !hasUserSelectedFilters &&
    (isSearchFocused || !selectedCategory)
      ? null
      : consolidatedFilters;

  // Handle navigation cleanup when screen loses focus
  // Similar to desktop version, but also check for APP_CONTAINER_ROUTES.BrandPage for mobile
  // Key difference: check navigation stack index to avoid popToTop when in nested navigation
  useFocusEffect(
    useCallback(() => {
      if (!orderGuideUnifiedSearchEnabled) {
        return;
      }

      // nothing happens when the screen is focused
      // we do the check and popToTop only on blur
      return () => {
        const parent = navigation.getParent?.() ?? navigation;
        const nextState = parent.getState();
        const nextRoute = getActiveRoute(nextState);

        // Check if we're going to brand page (any of the possible routes)
        const goingToBrandPage =
          nextRoute?.name === ORDER_GUIDE_ROUTES.BrandPage ||
          nextRoute?.name === CATALOG_ROUTES.BrandPage ||
          nextRoute?.name === APP_CONTAINER_ROUTES.BrandPage;

        // Check navigation stack index - if index > 0, we're in nested navigation
        // This means we might be navigating to/from brand page or other nested screens
        const navigationState = navigation.getState();
        const isNestedNavigation = navigationState && navigationState.index > 0;

        // Don't pop to top if:
        // 1. We're going to brand page, OR
        // 2. We're in nested navigation (prevents popToTop when coming back from brand page)
        if (!goingToBrandPage && !isNestedNavigation) {
          navigation.popToTop();
        }
      };
    }, [navigation, orderGuideUnifiedSearchEnabled]),
  );

  const onSelectCategory = (category: CatalogCategoryType | undefined) => {
    if (!category && !searchText) {
      searchInputRef.current?.focus();
    }
    setSelectedCategory(category);
  };

  const isItemInAnyOrderGuide = useCallback(
    (itemMetadata: ItemListOptionMetadata) => {
      return itemMetadata.skus.some(
        (sku) => variantPackPartitionMap[sku.variantPackUUID],
      );
    },
    [variantPackPartitionMap],
  );

  const renderNote = useMemo(() => {
    if (!searchOrderGuideActionsEnabled) {
      return undefined;
    }

    return (skuMetadata: ItemListOptionMetadata) => {
      const partitions = variantPackPartitionMap[skuMetadata.variantPackUUID];
      if (!partitions) {
        return null;
      }

      const collectionsWithMemos = new Set<string>();
      const partitionsWithMemos = new Set<string>();
      let hasRequest = false; // at least one request is enough to show the note
      let noteText = undefined;

      Object.entries(partitions).forEach(([partitionUUID, orderGuideItem]) => {
        const collectionUUID = partitionToCollectionMap.get(partitionUUID);

        if (!collectionUUID) {
          // should never happen, typescript check
          return;
        }

        const { memo, request } = orderGuideItem.order_guide_restaurant || {};
        if (memo) {
          collectionsWithMemos.add(collectionUUID);
          partitionsWithMemos.add(partitionUUID);
          noteText = memo;
        }
        if (request) {
          hasRequest = true;
        }
      });

      // No memos and no requests, so no note to show
      if (collectionsWithMemos.size === 0 && !hasRequest) {
        return null;
      }

      return (
        <View style={styles.searchNoteWrapper}>
          <SearchNote
            memoCollectionCount={collectionsWithMemos.size}
            memoPartitionCount={partitionsWithMemos.size}
            noteText={noteText}
            onPress={() =>
              // Wrap the single SKU so resolveOrderGuideSelection treats it as the only option
              handleAddNotePress({ ...skuMetadata, skus: [skuMetadata] })
            }
            showRequest={canWriteRequest && hasRequest}
          />
        </View>
      );
    };
  }, [
    canWriteRequest,
    handleAddNotePress,
    partitionToCollectionMap,
    searchOrderGuideActionsEnabled,
    styles.searchNoteWrapper,
    variantPackPartitionMap,
  ]);

  // swipe actions, which are only shown on mobile (not small web layouts)
  const listItemProps = useMemo<
    Partial<ListItemProps<ItemListOption>> | undefined
  >(() => {
    if (!swipeableActionsEnabled) {
      return undefined;
    }

    return {
      swipeableProps: {
        onSwipeableRightWillOpen: (_, swipeableRef) => {
          if (
            swipeableItemRef.current &&
            swipeableItemRef.current !== swipeableRef
          ) {
            closeSwipeableRef();
          }
          swipeableItemRef.current = swipeableRef;
        },
        renderRightActions: (option) => {
          const showNoteAction =
            option.metadata && isItemInAnyOrderGuide(option.metadata);

          if (!showNoteAction) {
            return undefined;
          }

          const drawerWidth = ACTION_BUTTON_WIDTH * 2;

          return (
            <View style={[styles.rightActionContainer, { width: drawerWidth }]}>
              <EssentialItemRightAction
                onPress={() => {
                  editEssentialItem(option.metadata!);
                }}
                trackingData={{
                  name: 'search_essential_item_right_action_button',
                  properties: {
                    variantPackUUID: option.metadata!.variantPackUUID,
                  },
                }}
              />
              <AddNoteRightAction
                onPress={() => handleAddNotePress(option.metadata!)}
                trackingData={{
                  name: 'search_add_note_right_action_button',
                  properties: {
                    variantPackUUID: option.metadata!.variantPackUUID,
                  },
                }}
              />
            </View>
          );
        },
      },
    };
  }, [
    closeSwipeableRef,
    handleAddNotePress,
    isItemInAnyOrderGuide,
    swipeableActionsEnabled,
    styles.rightActionContainer,
    editEssentialItem,
  ]);

  return (
    <PageContainer
      navigationHeaderProps={{
        contentContainerStyle: styles.navigationHeaderContentContainer,
        showBackButton: true,
        showShadow: true,
        subheader: categoriesLoading ? (
          <CategoryTabsSkeleton height={LIST_HEIGHT} />
        ) : categories ? (
          <CategoryTabs
            categories={categories}
            categoryCountsMap={categoryCountsMap}
            onSelectCategory={onSelectCategory}
            selectedCategory={selectedCategory}
          />
        ) : null,
        title: (
          <View style={styles.searchInputWrapper}>
            <ItemSearchInput
              autoFocus={!selectedCategory}
              clearSearch={clearSearch}
              containerStyle={styles.searchInputContainer}
              initialSearchText={searchText}
              inputRef={searchInputRef}
              loading={isSearchFocused && queryLoading}
              onBlur={() => setIsSearchFocused(false)}
              onFocus={() => setIsSearchFocused(true)}
              onSearchItems={searchItems}
              placeholder={i18n.t('search')}
            />
          </View>
        ),
      }}
      shouldDismissKeyboard
    >
      <KeyboardAvoidingView
        behavior={Platform.select({ android: undefined, ios: 'padding' })}
        enabled={isEmpty}
        style={styles.keyboardAvoidingView}
      >
        {catalogFilters ? (
          <SearchFilterPills
            category={selectedCategory}
            filters={catalogFilters}
            mainViewWrapperStyle={
              isSmallTabletLayout ? styles.tabletMainViewWrapperStyle : null
            }
            onChangeSortOrder={changeSortOrder}
            sortOrder={
              showSearchView || catalogCategoryListsEnabled
                ? searchSortOrder
                : undefined
            }
          />
        ) : orderGuideUnifiedSearchEnabled && !selectedCategory ? (
          <UnifiedSearchEmptyState />
        ) : null}
        {!showSearchView && selectedCategory && !catalogCategoryListsEnabled ? (
          <CatalogCategoryList categoryCatalog={categoryCatalog} />
        ) : (
          <SearchCatalogContent
            items={items}
            listItemProps={listItemProps}
            loading={queryLoading}
            onEndReached={
              catalogCategoryListsEnabled
                ? () => searchCatalog.fetchNextPage(searchSortOrder)
                : undefined
            }
            renderNote={renderNote}
            renderSource="search_supplier_catalog"
            searchText={searchText}
          />
        )}
        <ViewOrderButton style={{ marginBottom: bottom }} />
      </KeyboardAvoidingView>
      {addNoteState ? (
        <SearchAddNoteActionSheet
          info={addNoteState.info}
          onClose={() => setAddNoteState(null)}
          skusInOrderGuide={addNoteState.skusInOrderGuide}
        />
      ) : null}
    </PageContainer>
  );
};

export type SearchScreenProps = StackScreenProps<
  CatalogStackParams,
  CATALOG_ROUTES.Search
>;

export const SearchScreen: React.FC<SearchScreenProps> = ({
  route,
  navigation,
}) => {
  const { category, showCategories } = route.params || {};
  return (
    <SearchScreenComponent
      category={category}
      navigation={navigation}
      showCategories={showCategories}
    />
  );
};

const useStyles = createStyles((theme) => ({
  keyboardAvoidingView: {
    flex: 1,
  },
  navigationHeaderContentContainer: {
    paddingTop: theme.sk.spacing.spaceXsmall,
  },
  rightActionContainer: {
    flexDirection: 'row',
  },
  searchInputContainer: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
  searchInputWrapper: {
    flex: 1,
  },
  searchNoteWrapper: {
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  tabletMainViewWrapperStyle: {
    paddingLeft: theme.sk.spacing.spaceMedium,
    paddingRight: theme.sk.spacing.spaceMedium,
  },
}));
